import React, { SetStateAction, useEffect, useState } from "react"
import CategoryAction from "../CategoryAction/CategoryAction"
import SettingsContextMenu from "../SettingsContextMenu/SettingsContextMenu"
import SettingsContextMenuItem from "../SettingsContextMenuItem/SettingsContextMenuItem"
import SettingsButton from "../SettingsButton/SettingsButton"
import styles from "./HotKeysExtension.module.scss"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { faPen, faPlus, faTrashAlt } from "@fortawesome/pro-light-svg-icons"
import PopoverTrigger from "../PopoverTrigger/PopoverTrigger"
import HotKeysExtensionForm from "../HotKeysExtensionForm/HotKeysExtensionForm"
import { FastRepliesHotKeyWithId, HotKey } from "../../models/fastRepliesHotKeys"
import {
    useCreateHotKeyMutation,
    useDeleteHotKeyMutation,
    useUpdateHotKeyMutation
} from "../../api/controllers/fastRepliesHotKeys"
import { toHotKeyValues } from "./helpers/toHotKeyValues"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { selectFastRepliesCatalogHotKeys, selectFastRepliesShowTooltip } from "../../store/dialogs/selectors"
import { testId } from "../../utility/tests/testId/dialogs"
import { actions } from "../../store/dialogs/slice"
import { FastRepliesDataType, useFastRepliesData } from "./../../pages/FastRepliesSidebar/hooks/useFastRepliesData"

interface HotKeysExtensionProps {
    currentArticleId: string
    categoryId: string
    handleShowExtensionSettingsButton: (value: SetStateAction<boolean>) => void
    hotKey?: FastRepliesHotKeyWithId
    showHotKeyExtension?: boolean
}

const tNamespace = "dialogs:info.fast-replies.hot-keys."

const getSettings = (
    id: string,
    isHotKeyExist: boolean,
    t: TFunction,
    setShowForm: (value: boolean) => void,
    handleDelete: () => void
) => {
    const handleClick = () => {
        setShowForm(true)
    }

    if (!isHotKeyExist) {
        return [
            <SettingsContextMenuItem
                text={t(`${tNamespace}actions.create`)}
                id={`${id}-createHotKey`}
                key={`${id}-createHotKey`}
                icon={faPlus}
                onClick={handleClick}
                testId={testId.fastRepliesHotKeyCreateAction}
            />
        ]
    } else {
        return [
            <SettingsContextMenuItem
                text={t(`${tNamespace}actions.edit`)}
                id={`${id}-editHotKey`}
                key={`${id}-editHotKey`}
                icon={faPen}
                onClick={handleClick}
                testId={testId.fastRepliesHotKeyEditAction}
                onHoverActive
            />,
            <SettingsContextMenuItem
                danger
                text={t(`${tNamespace}actions.delete`)}
                id={`${id}-deleteHotKey`}
                key={`${id}-deleteHotKey`}
                icon={faTrashAlt}
                onClick={handleDelete}
                testId={testId.fastRepliesHotKeyDeleteAction}
            />
        ]
    }
}

const HotKeysExtension: React.FC<HotKeysExtensionProps> = props => {
    const {
        currentArticleId,
        categoryId,
        hotKey,
        showHotKeyExtension = false,
        handleShowExtensionSettingsButton
    } = props

    const { t } = useTranslation()

    const dispatch = useAppDispatch()
    const [showForm, setShowForm] = useState(false)
    const showTooltip = useAppSelector(selectFastRepliesShowTooltip)

    const hidePopover = () => {
        setShowForm(false)
        handleShowExtensionSettingsButton(false)
        dispatch(actions.setFastRepliesTooltipState(true))
    }
    const [loading, setLoading] = useState(false)
    const [hotKeyArticleTitle, setHotKeyArticleTitle] = useState("")
    const hotKeys = useAppSelector(selectFastRepliesCatalogHotKeys)
    const resetArticleTitle = () => setHotKeyArticleTitle("")

    const [createHotKey] = useCreateHotKeyMutation()
    const [updateHotKey] = useUpdateHotKeyMutation()
    const [deleteHotKey] = useDeleteHotKeyMutation()

    const { articleNodes: articles } = useFastRepliesData(FastRepliesDataType.ARTICLES)

    const handleSetHotKey = async (hotKeyToSet: HotKey, rewrite: boolean) => {
        setLoading(true)

        const existedHotKey = hotKeys.find(hotKey => hotKey.CombinationName === hotKeyToSet.CombinationName)

        if (existedHotKey?.ArticleSymbol && existedHotKey.ArticleSymbol !== currentArticleId && !rewrite) {
            const article = articles.find(article => article.data.symbolCode === existedHotKey.ArticleSymbol)
            const articleTitle = article?.data.title

            if (articleTitle) {
                setHotKeyArticleTitle(articleTitle)
                setLoading(false)
                dispatch(actions.setFastRepliesTooltipState(true))
                return
            }
        }

        if (existedHotKey?.Id && rewrite) {
            await deleteHotKey({ id: existedHotKey.Id, articles })
            resetArticleTitle()
        }

        if (hotKey) {
            updateHotKey({
                ...hotKey,
                ...hotKeyToSet,
                articles
            }).finally(() => {
                setLoading(false)
                hidePopover()
            })
        } else {
            createHotKey({
                ...hotKeyToSet,
                ArticleSymbol: currentArticleId,
                CatalogSymbol: categoryId,
                articles
            }).finally(() => {
                setLoading(false)
                hidePopover()
            })
        }
    }

    const handleDeleteHotKey = () => {
        hotKey && deleteHotKey({ id: hotKey.Id, articles })
        handleShowExtensionSettingsButton(false)
    }

    useEffect(() => {
        if (!showHotKeyExtension) {
            dispatch(actions.setFastRepliesTooltipState(true))
        }
    }, [showHotKeyExtension])

    return (
        <div className={styles.actionsContainer}>
            <span className={styles.actionsContainer__savedCombination}>{hotKey?.CombinationName}</span>
            <CategoryAction id={`${currentArticleId}-hot-key`} isHover>
                <PopoverTrigger
                    id="fast-replies-hot-key-form"
                    content={
                        <HotKeysExtensionForm
                            id={currentArticleId}
                            hidePopover={hidePopover}
                            handleSetHotKey={handleSetHotKey}
                            hotKey={toHotKeyValues(hotKey)}
                            loading={loading}
                            articleTitle={hotKeyArticleTitle}
                            reset={resetArticleTitle}
                        />
                    }
                    trigger="click"
                    placement="auto"
                    show={showForm}
                    className={styles.actionsContainer__hotKeyForm}
                >
                    <SettingsContextMenu
                        placement="bottom-start"
                        items={getSettings(currentArticleId, !!hotKey, t, setShowForm, handleDeleteHotKey)}
                        onHide={() => {
                            handleShowExtensionSettingsButton(false)
                        }}
                    >
                        <SettingsButton
                            id={`${currentArticleId}-hot-key`}
                            tooltipText={showTooltip ? t(`${tNamespace}tooltip`) : ""}
                            tooltipClassName={styles.actionsContainer__tooltip}
                            onClick={() => {
                                handleShowExtensionSettingsButton(prev => !prev)
                                dispatch(actions.setFastRepliesTooltipState(false))
                            }}
                        />
                    </SettingsContextMenu>
                </PopoverTrigger>
            </CategoryAction>
        </div>
    )
}

export default HotKeysExtension
